import { getBackendConfig, apiFetch } from 'ts-core';

interface CachedMetadata {
  data: any;
  timestamp: number;
  hostname: string;
  apiKey: string;
}

interface CategoryItemResponse {
  slug: string;
  name_en?: string;
  name_de?: string;
  name_it?: string;
  name_fr?: string;
  name_es?: string;
  name_pt?: string;
  icon?: string;
  text_icon?: string;
  text_icon_literal?: string;
  color?: string;
  sort_order?: number;
  is_system?: boolean;
  children?: CategoryItemResponse[];
}

interface CategoriesApiResponse {
  canonical?: CategoryItemResponse[];
  partner?: CategoryItemResponse[];
}

interface TagItemResponse {
  id: string;
  name: string;
  slug: string;
}

interface CategorizedTagsApiResponse {
  system?: TagItemResponse[];
  user?: TagItemResponse[];
  general?: TagItemResponse[];
  partner?: TagItemResponse[];
}

/**
 * Metadata loader with localStorage caching.
 * Handles fetching and caching of event metadata.
 */
export class MetadataLoader {
  private apiKey: string;
  private cacheKey: string;
  private cacheTTL: number = 24 * 60 * 60 * 1000; // 24 hours

  constructor(apiKey: string) {
    this.apiKey = apiKey;
    
    // Create cache key based on hostname and API key
    const hostname = typeof window !== 'undefined' ? window.location.hostname : 'unknown';
    this.cacheKey = `balloon-events-metadata-${hostname}-${apiKey}`;
  }

  /**
   * Load metadata (from cache or API)
   * @param options - Options including AbortSignal for cancellation
   */
  public async load(options: { signal?: AbortSignal } = {}): Promise<any> {
    console.log('[MetadataLoader] Starting metadata load...', {
      apiKey: this.apiKey,
      cacheKey: this.cacheKey,
      hasSignal: !!options.signal,
    });
    
    // Check if the operation was aborted before starting
    if (options.signal?.aborted) {
      throw new DOMException('Metadata loading was aborted', 'AbortError');
    }
    
    // Try to load from cache first
    const cached = this.loadFromCache();

    if (cached) {
      console.log('[MetadataLoader] Serving cached metadata, refreshing in background', {
        timestamp: cached.timestamp,
        age: Date.now() - cached.timestamp,
      });
      // Always revalidate in background so new tags/categories are picked up on next render
      this.fetchFromAPI(options.signal)
        .then((metadata) => {
          if (!options.signal?.aborted) {
            this.saveToCache(metadata);
          }
        })
        .catch(() => {
          // background refresh failure is non-fatal
        });
      return cached.data;
    }

    // No cache — fetch fresh metadata from API
    console.log('[MetadataLoader] Fetching fresh metadata from API');
    
    const metadata = await this.fetchFromAPI(options.signal);
    
    // Cache the result if not aborted
    if (!options.signal?.aborted) {
      this.saveToCache(metadata);
    }
    
    return metadata;
  }

  /**
   * Fetch metadata from the API
   * @param signal - Optional AbortSignal to cancel the request
   */
  private async fetchFromAPI(signal?: AbortSignal): Promise<any> {
    try {
      // Use the backend config from the core library
      const config = getBackendConfig();
      
      // Construct metadata URL from baseUrl
      const metadataUrl = `${config.baseUrl}/api/metadata`;
      
      console.log('[MetadataLoader] Fetching from:', metadataUrl);
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort('Request timeout'), 10000); // 10 second timeout
      
      // Set up abort handling
      if (signal) {
        if (signal.aborted) {
          throw new DOMException('Metadata loading was aborted', 'AbortError');
        }
        signal.addEventListener('abort', () => {
          controller.abort(signal.reason || 'Operation aborted by user');
        }, { once: true });
      }
      
      const response = await apiFetch(metadataUrl, {
        apiKey: config.apiKey,
        signal: controller.signal,
      }).finally(() => clearTimeout(timeoutId));

      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }

      const metadata = await response.json();
      
      console.log('[MetadataLoader] Fetched metadata:', metadata);
      
      // Fetch and merge v2 endpoints
      await this.mergeV2Data(metadata, config, controller.signal);
      
      return metadata;
    } catch (error: any) {
      // Don't log abort errors as errors - they're expected during cleanup
      if (error.name === 'AbortError') {
        console.log('[MetadataLoader] Fetch aborted:', error.message);
      } else {
        console.error('[MetadataLoader] Error fetching metadata:', error);
      }
      throw error;
    }
  }

  /**
   * Fetch and merge v2 categories and tags into metadata
   */
  private async mergeV2Data(
    metadata: any,
    config: { baseUrl: string; apiKey: string },
    signal: AbortSignal
  ): Promise<void> {
    try {
      const [categoriesResult, tagsResult] = await Promise.allSettled([
        this.fetchV2Categories(config, signal),
        this.fetchV2Tags(config, signal),
      ]);

      if (categoriesResult.status === 'fulfilled' && categoriesResult.value) {
        metadata.data.categories = categoriesResult.value;
        console.log('[MetadataLoader] Merged v2 categories:', categoriesResult.value.length);
      }

      if (tagsResult.status === 'fulfilled' && tagsResult.value) {
        metadata.data.tags = tagsResult.value;
        console.log('[MetadataLoader] Merged v2 tags:', tagsResult.value.length);
      }
    } catch (error) {
      console.error('[MetadataLoader] Error merging v2 data:', error);
    }
  }

  /**
   * Fetch v2 categories and flatten into array
   */
  private async fetchV2Categories(
    config: { baseUrl: string; apiKey: string },
    signal: AbortSignal
  ): Promise<any[] | null> {
    try {
      const url = `${config.baseUrl}/api/v2/categories`;
      const response = await apiFetch(url, {
        apiKey: config.apiKey,
        headers: { Accept: 'application/json' },
        signal,
      });

      if (!response.ok) {
        console.error('[MetadataLoader] Failed to fetch v2/categories:', response.statusText);
        return null;
      }

      const data: CategoriesApiResponse = await response.json();
      return this.mergeCategories(data);
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        console.error('[MetadataLoader] Error fetching v2/categories:', error);
      }
      return null;
    }
  }

  /**
   * Fetch v2 tags and flatten into array
   */
  private async fetchV2Tags(
    config: { baseUrl: string; apiKey: string },
    signal: AbortSignal
  ): Promise<any[] | null> {
    try {
      const url = `${config.baseUrl}/api/v2/tags`;
      const response = await apiFetch(url, {
        apiKey: config.apiKey,
        headers: { Accept: 'application/json' },
        signal,
      });

      if (!response.ok) {
        console.error('[MetadataLoader] Failed to fetch v2/tags:', response.statusText);
        return null;
      }

      const data: CategorizedTagsApiResponse = await response.json();
      return this.mergeTags(data);
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        console.error('[MetadataLoader] Error fetching v2/tags:', error);
      }
      return null;
    }
  }

  /**
   * Merge canonical and partner categories with nested children
   */
  private mergeCategories(data: CategoriesApiResponse): any[] {
    const merged: any[] = [];
    const seen = new Set<string>();

    const transformCategory = (cat: CategoryItemResponse): any => {
      return {
        id: cat.slug,
        slug: cat.slug,
        color: cat.color || '',
        textIcon: cat.text_icon || '',
        textIconLiteral: cat.text_icon_literal || '',
        nameEn: cat.name_en || '',
        nameDe: cat.name_de || '',
        nameIt: cat.name_it || '',
        nameFr: cat.name_fr || '',
        nameEs: cat.name_es || '',
        namePt: cat.name_pt || '',
        icon: cat.icon || '',
        sortOrder: cat.sort_order ?? 0,
        isSystem: cat.is_system ?? false,
        children: (cat.children || []).map(child => transformCategory(child)),
      };
    };

    const addCategory = (cat: CategoryItemResponse): void => {
      const id = cat.slug;
      if (seen.has(id)) return;
      seen.add(id);
      merged.push(transformCategory(cat));
    };

    // Process canonical categories with nested children
    for (const cat of data.canonical || []) {
      addCategory(cat);
    }
    // Then process partner categories with nested children
    for (const cat of data.partner || []) {
      addCategory(cat);
    }

    return merged;
  }

  /**
   * Merge all tag types into flat array
   */
  private mergeTags(data: CategorizedTagsApiResponse): any[] {
    const merged: any[] = [];
    const seen = new Set<string>();

    const addTags = (tags: TagItemResponse[] | undefined, tagType: string): void => {
      if (!tags) return;
      for (const tag of tags) {
        if (seen.has(tag.id)) continue;
        seen.add(tag.id);
        merged.push({
          id: tag.id,
          name: tag.name,
          slug: tag.slug,
          tagType,
        });
      }
    };

    addTags(data.system, 'system');
    addTags(data.general, 'general');
    addTags(data.partner, 'partner');
    addTags(data.user, 'user');

    return merged;
  }

  /**
   * Load metadata from localStorage cache
   */
  private loadFromCache(): CachedMetadata | null {
    if (typeof localStorage === 'undefined') {
      return null;
    }

    try {
      const cached = localStorage.getItem(this.cacheKey);
      
      if (!cached) {
        return null;
      }

      return JSON.parse(cached) as CachedMetadata;
    } catch (error) {
      console.error('[MetadataLoader] Error loading from cache:', error);
      return null;
    }
  }

  /**
   * Save metadata to localStorage cache
   */
  private saveToCache(metadata: any): void {
    if (typeof localStorage === 'undefined') {
      return;
    }

    try {
      const hostname = typeof window !== 'undefined' ? window.location.hostname : 'unknown';
      
      const cached: CachedMetadata = {
        data: metadata,
        timestamp: Date.now(),
        hostname,
        apiKey: this.apiKey,
      };

      localStorage.setItem(this.cacheKey, JSON.stringify(cached));
      
      if (import.meta.env.NODE_ENV === 'development') {
        console.log('[MetadataLoader] Metadata cached');
      }
    } catch (error) {
      console.error('[MetadataLoader] Error saving to cache:', error);
    }
  }

  /**
   * Check if cached metadata is expired
   */
  private isCacheExpired(cached: CachedMetadata): boolean {
    const now = Date.now();
    const age = now - cached.timestamp;
    
    return age > this.cacheTTL;
  }

  /**
   * Clear cached metadata
   */
  public clearCache(): void {
    if (typeof localStorage === 'undefined') {
      return;
    }

    try {
      localStorage.removeItem(this.cacheKey);
      
      if (import.meta.env.NODE_ENV === 'development') {
        console.log('[MetadataLoader] Cache cleared');
      }
    } catch (error) {
      console.error('[MetadataLoader] Error clearing cache:', error);
    }
  }

  /**
   * Set cache TTL (in milliseconds)
   */
  public setCacheTTL(ttl: number): void {
    this.cacheTTL = ttl;
  }

  /**
   * Get cache TTL (in milliseconds)
   */
  public getCacheTTL(): number {
    return this.cacheTTL;
  }
}
